import React, { useEffect, useMemo, useRef, useState, useLayoutEffect } from 'react';
import { Box, Typography, TextField, MenuItem, Stack, Tooltip } from '@mui/material';
import api from '../../services/api';
import { getEmployeeFullName } from '../../utils/names';

function parseDate(s){
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d) ? null : d;
}

function formatYmd(d){
  const y = d.getFullYear();
  const m = String(d.getMonth()+1).padStart(2,'0');
  const day = String(d.getDate()).padStart(2,'0');
  return `${y}-${m}-${day}`;
}

function daysBetween(from, to){
  const res = [];
  const d = new Date(from);
  while(d <= to){
    res.push(new Date(d));
    d.setDate(d.getDate()+1);
  }
  return res;
}


export default function AttendanceMap({ year: propYear, departmentId, employeesById: propEmployeesById, from, to, showYearSelector = true, refreshTrigger }){
  const today = new Date();
  const initialYear = propYear || today.getFullYear();
  const [year, setYear] = useState(initialYear);
  const [leaves, setLeaves] = useState([]);
  const scrollRef = useRef(null);
  const [holidayByYmd, setHolidayByYmd] = useState({});
  const employeesById = propEmployeesById || {};

  useEffect(()=>{
    (async()=>{
      try{
        const fromStr = (from instanceof Date ? from : null) ? formatYmd(from) : new Date(year,0,1).toISOString().slice(0,10);
        const toStr = (to instanceof Date ? to : null) ? formatYmd(to) : new Date(year,11,31).toISOString().slice(0,10);
        const all = await api.get('/leave-requests', { params: { from: fromStr, to: toStr } });
        const arr = Array.isArray(all)?all:[];
        setLeaves(arr);
      }catch(e){ console.warn('attendance load failed', e); }
    (async()=>{
      try{
        let hol;
        try {
          hol = await api.get('/holidays/enriched', { params: { year } });
        } catch(err) {
          if (err?.response?.status === 404) {
            hol = await api.get('/holidays');
          } else {
            throw err;
          }
        }
        const map = {};
        (Array.isArray(hol)?hol:[]).forEach(h=>{
          const d = new Date(h.date||h.Date);
          if(!isNaN(d)) map[formatYmd(d)] = h.localName || h.name || h.Name || 'Holiday';
        });
        setHolidayByYmd(map);
      }catch(e){ console.warn('holidays load failed', e); }
    })();
    })();
  },[year, from, to, refreshTrigger]);

  const rangeDays = useMemo(()=>{
    const f = (from instanceof Date ? from : null) || new Date(year,0,1);
    const t = (to instanceof Date ? to : null) || new Date(year,11,31);
    return daysBetween(f, t);
  },[year, from, to]);

  // Column width CSS: responsive — up to 2 months fill available width; beyond that fixed 18px per day
  const dayColCss = useMemo(()=>{
    const days = rangeDays.length;
    // Use CSS var if provided by container, else fallback to 980px
    const cssVar = typeof window !== 'undefined' ? getComputedStyle(document.documentElement).getPropertyValue('--attendance-width') : '';
    const maxWidth = cssVar ? cssVar.trim() : '980px';
    if (days <= 62) {
      return `calc(${maxWidth} / ${Math.max(1, days)})`;
    }
    return '18px';
  }, [rangeDays.length]);

  const rows = useMemo(()=>{
    const f = new Date(year,0,1); const t = new Date(year,11,31);
    const fY = formatYmd(f); const tY = formatYmd(t);
    const inRange = (r)=>{
      const s = (r.startDateLocalYmd||r.StartDateLocalYmd||r.startDate||r.StartDate);
      const e = (r.endDateLocalYmd||r.EndDateLocalYmd||r.endDate||r.EndDate);
      const sY = s ? String(s).substring(0,10) : '';
      const eY = e ? String(e).substring(0,10) : '';
      if(!sY||!eY) return false;
      return sY <= tY && eY >= fY;
    };
    const deptFilter = (r)=>{
      if(!departmentId) return true;
      const emp = employeesById?.[r.employeeId||r.EmployeeId];
      return (r.departmentId||r.DepartmentId||emp?.departmentId) === departmentId;
    };
    const statusOk = (r)=>{
      const s = (r.status||r.Status||'').toString().toLowerCase();
      // Hide non-impacting requests: rejected/canceled
      return !(s==='rejected' || s==='cancelled' || s==='canceled');
    };
    const filtered = leaves.filter(inRange).filter(deptFilter).filter(statusOk);
    const byEmp = new Map();
    filtered.forEach(r=>{
      const id = r.employeeId||r.EmployeeId;
      if(!byEmp.has(id)) byEmp.set(id, []);
      byEmp.get(id).push(r);
    });
    // Hide employees that end up with no impacting requests
    const rows = Array.from(byEmp.entries()).map(([empId, reqs])=>({ empId, reqs }));
    return rows.filter(r=> (r.reqs&&r.reqs.length>0));
  },[leaves, year, departmentId, employeesById]);

  const colorFor = (status, leaveTypeCode)=>{
    const s = (status||'').toString().toLowerCase();
    const typeCode = (leaveTypeCode||'').toString().toUpperCase();

    // Sick leave gets blue color regardless of status (it's auto-approved)
    if (typeCode === 'SL') return (theme)=> theme.palette.info.main;

    if (s==='approved') return (theme)=> theme.palette.success.main;
    if (s==='pending' || s==='pendingdm' || s==='pendingboard') return (theme)=> theme.palette.warning.main;
    if (s==='cancelrequested') return (theme)=> theme.palette.secondary.main;
    if (s==='rejected') return (theme)=> theme.palette.error.main;
    // cancelled and other statuses are shown transparent (no fill)
    return 'transparent';
  };

  // Month groups for header labels spanning the days of each month
  const monthGroups = useMemo(()=>{
    const groups = [];
    if(rangeDays.length===0) return groups;
    let startIdx = 0;
    for(let i=1;i<=rangeDays.length;i++){
      const prev = rangeDays[i-1];
      const curr = rangeDays[i];
      const boundary = i===rangeDays.length || curr.getMonth()!==prev.getMonth();
      if(boundary){
        groups.push({
          start: startIdx,
          end: i-1,
          label: prev.toLocaleString('default',{ month:'short', year:'numeric'})
        });
        startIdx = i;
      }
    }
    return groups;
  },[rangeDays]);

  // Row heights for alignment between fixed left column and scrollable right grid
  const H_MONTH = 24; // px
  const H_DAYS = 20;
  const H_SEP = 6;
  const H_ROW = 18;

  // Auto-scroll and center around today using actual cell position (with a second pass)
  useLayoutEffect(()=>{
    const el = scrollRef.current;
    if(!el || rangeDays.length===0) return;
    const center = () => {
      const dayIndex = rangeDays.findIndex(d=>d.toDateString()===today.toDateString());
      if (dayIndex < 0) return;
      const target = el.querySelector(`[data-col="${dayIndex}"]`);
      if (!target) return;
      const elRect = el.getBoundingClientRect();
      const rect = target.getBoundingClientRect();
      const desired = el.scrollLeft + (rect.left - elRect.left) - (el.clientWidth/2 - rect.width/2);
      const clamped = Math.max(0, Math.min(el.scrollWidth - el.clientWidth, desired));
      el.scrollLeft = clamped;
    };
    center();
    const t = setTimeout(center, 0);
    return () => clearTimeout(t);
  }, [year, rangeDays]);

  const yearOptions = Array.from({length: 5}, (_,i)=> today.getFullYear() - 2 + i);

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb:1 }}>
        <Typography variant="h6" sx={{ fontWeight:600, flex:1 }}>Leave Requests Map</Typography>
        {showYearSelector && (
          <TextField select size="small" label="Year" value={year} onChange={(e)=>setYear(parseInt(e.target.value))}>
            {yearOptions.map(y=> <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </TextField>
        )}
      </Stack>

      {/* Legend */}
      <Stack direction="row" spacing={2} sx={{ mb:2, flexWrap:'wrap' }}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Box sx={{ width:14, height:14, bgcolor:'success.main', borderRadius:0.5 }} />
          <Typography variant="caption">Approved</Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Box sx={{ width:14, height:14, bgcolor:'warning.main', borderRadius:0.5 }} />
          <Typography variant="caption">Pending</Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Box sx={{ width:14, height:14, bgcolor:'info.main', borderRadius:0.5 }} />
          <Typography variant="caption">Sick Leave</Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Box sx={{ width:14, height:14, bgcolor:'secondary.main', borderRadius:0.5 }} />
          <Typography variant="caption">Cancel Requested</Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Box sx={{ width:14, height:14, bgcolor:'error.light', borderRadius:0.5 }} />
          <Typography variant="caption">Holiday</Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Box sx={{ width:14, height:14, bgcolor:'action.selected', borderRadius:0.5, display:'flex', alignItems:'center', justifyContent:'center' }}>
            <Typography variant="caption" sx={{ fontSize:10, color:'text.secondary' }}>×</Typography>
          </Box>
          <Typography variant="caption">Weekend</Typography>
        </Stack>
      </Stack>

      {/* Two-column layout: fixed left names column + scrollable right grid */}
      <Box sx={{ display:'flex', alignItems:'flex-start' }}>
        {/* Left fixed column (not horizontally scrollable) */}
        <Box sx={{ width:240, minWidth:240, flex:'0 0 240px', bgcolor:'background.paper', position:'sticky', left:0, zIndex:1 }}>
          <Box sx={{ height: H_MONTH, display:'flex', alignItems:'center', pl:1, fontSize:12, color:'text.secondary', borderRight: (theme) => `1px solid ${theme.palette.divider}` }}>Employee</Box>
          <Box sx={{ height: H_DAYS, borderRight: (theme) => `1px solid ${theme.palette.divider}` }} />
          <Box sx={{ height: H_SEP, borderRight: (theme) => `1px solid ${theme.palette.divider}` }} />
          {rows.map(({empId, reqs})=>{
            const emp = employeesById?.[empId];
            const reqName = (Array.isArray(reqs) && reqs.length>0) ? (reqs[0].employeeName || reqs[0].EmployeeName) : '';
            const computed = emp ? (emp.fullName || `${emp.person?.firstName || emp.firstName || ''} ${emp.person?.lastName || emp.lastName || ''}`.trim()) : '';
            const name = (computed || reqName || empId);
            return (
              <Box key={empId} sx={{ pr:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', height:H_ROW, lineHeight:`${H_ROW}px`, borderRight: (theme) => `1px solid ${theme.palette.divider}`, fontSize:12, color:'text.primary' }}>
                {name}
              </Box>
            );
          })}
        </Box>

        {/* Right scrollable area */}
        <Box ref={scrollRef} sx={{ overflowX:'auto', flex:1 }}>
          {/* Month labels row */}
          <Box sx={{ display:'grid', gridTemplateColumns:`repeat(${rangeDays.length}, ${dayColCss})`, gap:0.5, width: rangeDays.length <= 62 ? '980px' : 'auto' }}>
            {monthGroups.map((g, idx)=> (
              <Box key={`m-${idx}`} sx={{ gridColumn: `${g.start+1} / span ${g.end-g.start+1}`, textAlign:'center', fontSize:12, color:'text.secondary', height:H_MONTH, lineHeight: `${H_MONTH}px` }}>
                {g.label}
              </Box>
            ))}
          </Box>
          {/* Day numbers row */}
          <Box sx={{ display:'grid', gridTemplateColumns:`repeat(${rangeDays.length}, ${dayColCss})`, gap:0.5, width: rangeDays.length <= 62 ? '980px' : 'auto' }}>
            {rangeDays.map((d,i)=>{
              const isToday = d.toDateString()===today.toDateString();
              const dow = d.getDay();
              const isWeekend = dow===0 || dow===6;
              return (
                <Box key={`d-${i}`} data-col={i} sx={{ position:'relative', height:H_DAYS, lineHeight:`${H_DAYS}px`, textAlign:'center' }}>
                  <Box sx={{ display:'inline-block', px:0.75, py:0.1, borderRadius:1, fontSize:11, color: isWeekend ? 'error.main' : 'text.primary', fontWeight: 500, backgroundColor: isToday ? 'action.hover' : 'transparent', border: theme => (isToday ? `1px solid ${theme.palette.divider}` : 'none') }}>
                    {d.getDate()}
                  </Box>
                </Box>
              );
            })}
          </Box>
          {/* Separator row */}
          <Box sx={{ display:'grid', gridTemplateColumns:`repeat(${rangeDays.length}, ${dayColCss})`, gap:0.5, width: rangeDays.length <= 62 ? '980px' : 'auto' }}>
            {rangeDays.map((_,i)=> (
              <Box key={`s-${i}`} sx={{ height: H_SEP, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }} />
            ))}
          </Box>
          {/* Data rows */}
          {rows.length===0 ? (
            <Box sx={{ p:2, color:'text.secondary' }}>No data for the selected period</Box>
          ) : (
            rows.map(({empId, reqs})=> (
              <Box key={`r-${empId}`} sx={{ display:'grid', gridTemplateColumns:`repeat(${rangeDays.length}, ${dayColCss})`, gap:0.5, width: rangeDays.length <= 62 ? '980px' : 'auto', height: H_ROW }}>
                {rangeDays.map((d,i)=>{
                  const day = formatYmd(d);
                  const rec = reqs.find(x=>{
                    const s = (x.startDateLocalYmd || x.StartDateLocalYmd || (x.startDate||x.StartDate));
                    const e = (x.endDateLocalYmd || x.EndDateLocalYmd || (x.endDate||x.EndDate));
                    const sY = s ? String(s).substring(0,10) : '';
                    const eY = e ? String(e).substring(0,10) : '';
                    return sY <= day && eY >= day;
                  });
                  const dow = d.getDay();
                  const isWeekend = dow===0 || dow===6;
                  const holiday = holidayByYmd[day];

                  // Background logic
                  const leaveTypeCode = rec ? (rec.leaveTypeCode || rec.LeaveTypeCode || '') : '';
                  const bg = rec
                    ? colorFor(rec.status||rec.Status, leaveTypeCode)
                    : (holiday ? ((theme)=> theme.palette.error.light) : (isWeekend ? ((theme)=> theme.palette.action.selected) : 'transparent'));

                  // Tooltip content: request hint + optional holiday
                  let tooltip = null;
                  if (rec) {
                    const rid = rec.id || rec.Id;
                    const rs = (rec.startDate||rec.StartDate||'').toString().substring(0,10);
                    const re = (rec.endDate||rec.EndDate||'').toString().substring(0,10);
                    const rtype = rec.leaveTypeName || rec.LeaveTypeName || rec.leaveTypeId || rec.LeaveTypeId || '';
                    const rstatus = rec.status || rec.Status || '';
                    tooltip = `Request #${rid ?? ''}: ${rs} – ${re} • ${rtype} • ${rstatus}`.trim();
                    if (holiday) tooltip += `\nHoliday: ${holiday}`;
                  } else if (holiday) {
                    tooltip = `Holiday: ${holiday}`;
                  }

                  const cell = (
                    <Box key={`${empId}-${i}`} sx={{
                      position:'relative',
                      height:'100%',
                      boxSizing:'border-box',
                      border: (theme) => `1px solid ${theme.palette.divider}`,
                      backgroundColor: bg
                    }}>
                      {isWeekend && !rec && (
                        <Box sx={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', color:'text.secondary', fontSize:12, lineHeight:1 }}>×</Box>
                      )}
                    </Box>
                  );

                  return tooltip ? (
                    <Tooltip key={`${empId}-${i}`} title={<span style={{ whiteSpace:'pre-line' }}>{tooltip}</span>} arrow>
                      {cell}
                    </Tooltip>
                  ) : cell;
                })}
              </Box>
            ))
          )}
        </Box>
      </Box>
    </Box>
  );
}

